import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Globe, EyeOff, Package, Pencil, RotateCcw, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/shared";
import {
  useApproveProductMutation,
  useDeleteAdminProductMutation,
  useSetProductVisibilityMutation,
} from "@/hooks/queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { channelStockService } from "@/services/channelStockService";
import { productsService } from "@/services/productsService";
import { resolveUploadUrl } from "@/services/filesService";
import { ProductStatus } from "@/types/enums";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

const statusBadge: Record<string, string> = {
  approved: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
  needs_revision: "bg-orange-500/10 text-orange-600 border-orange-500/20",
};

function mapStatus(status: ProductStatus): string {
  switch (status) {
    case ProductStatus.Approved:
      return "approved";
    case ProductStatus.Rejected:
      return "rejected";
    case ProductStatus.NeedsRevision:
      return "needs_revision";
    default:
      return "pending";
  }
}

function ChannelStockCard({ productId }: { productId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "channel-stock", productId],
    queryFn: () => channelStockService.getProduct(productId),
    enabled: productId > 0,
  });
  const [quantity, setQuantity] = useState("");

  useEffect(() => {
    if (data) setQuantity(String(data.adminQuantity));
  }, [data]);

  const save = useMutation({
    mutationFn: () => channelStockService.setAdmin(productId, Number(quantity) || 0),
    onSuccess: (next) => {
      queryClient.setQueryData(["admin", "channel-stock", productId], next);
      toast({ title: "Channel stock updated" });
    },
    onError: (error) => {
      toast({ title: "Could not update channel stock", description: getApiErrorMessage(error), variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Channel stock</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Admin pool and party balances. Store shelf stock ({data?.storeStock ?? "—"}) stays available for customer orders.
        </p>
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label>Admin master quantity</Label>
              <Input
                type="number"
                min={0}
                className="w-40"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value.replace(/[^\d]/g, ""))}
              />
            </div>
            <Button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending || quantity === "" || Number(quantity) === data?.adminQuantity}
            >
              {save.isPending ? "Saving…" : "Set master stock"}
            </Button>
          </div>
        )}
        <div>
          <p className="font-medium">Held by parties</p>
          {data && data.allocations.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {data.allocations.map((row) => (
                <li key={row.partyId} className="flex justify-between rounded-md border px-3 py-2">
                  <span>{row.businessName} · {row.levelText}</span>
                  <span className="font-medium">{row.quantity}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-muted-foreground">No distributor, wholesaler, or retailer holds this product yet.</p>
          )}
        </div>
        {data && data.movements.length > 0 && (
          <div>
            <p className="font-medium">Recent movements</p>
            <ul className="mt-2 space-y-1 text-muted-foreground">
              {data.movements.map((row) => (
                <li key={row.id}>
                  {row.holderName} {row.quantityDelta > 0 ? "+" : ""}{row.quantityDelta} → {row.balanceAfter}
                  {row.note ? ` · ${row.note}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const AdminProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const productId = id ? Number(id) : 0;
  const approveMutation = useApproveProductMutation();
  const visibilityMutation = useSetProductVisibilityMutation();
  const deleteMutation = useDeleteAdminProductMutation();
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState<"reject" | "revision" | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: product, isLoading } = useQuery({
    queryKey: ["admin", "product", productId],
    queryFn: () => productsService.getById(productId),
    enabled: productId > 0,
  });

  const status = product ? mapStatus(product.status) : "pending";
  const canReview = status === "pending" || status === "needs_revision";
  const isApproved = status === "approved";

  const refetch = () => queryClient.invalidateQueries({ queryKey: ["admin", "product", productId] });

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync({ id: productId, action: "Approve", approve: true });
      toast({ title: "Product approved and published" });
      refetch();
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handlePublish = async (isActive: boolean) => {
    try {
      await visibilityMutation.mutateAsync({ id: productId, isActive });
      toast({ title: isActive ? "Product published" : "Product unpublished" });
      refetch();
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(productId);
      toast({ title: "Product deleted" });
      navigate("/admin/products");
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleAction = async (action: "Reject" | "RequestRevision") => {
    if (!notes.trim()) return;
    try {
      await approveMutation.mutateAsync({
        id: productId,
        action,
        approve: false,
        rejectionReason: notes.trim(),
      });
      toast({
        title: action === "Reject" ? "Product rejected" : "Revision requested",
        variant: action === "Reject" ? "destructive" : "default",
      });
      navigate("/admin/products");
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Product not found.</p>
        <Button variant="outline" asChild><Link to="/admin/products">Back to products</Link></Button>
      </div>
    );
  }

  const images = product.images?.length
    ? product.images
    : product.primaryImage
      ? [{ imageUrl: product.primaryImage, displayOrder: 0, isPrimary: true }]
      : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/products"><ArrowLeft className="h-5 w-5" /></Link>
        </Button>
        <PageHeader
          title={product.name}
          description={`${product.storeName} · ${product.categoryName}`}
          className="flex-1 min-w-0"
        />
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={statusBadge[status]}>{status.replace("_", " ")}</Badge>
          {isApproved && (
            <Badge variant="outline" className={product.isActive ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground"}>
              {product.isActive ? "Published" : "Unpublished"}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" className="gap-1" asChild>
          <Link to={`/admin/products/${productId}/edit`}>
            <Pencil className="h-4 w-4" /> Edit product
          </Link>
        </Button>
        {isApproved && (
          product.isActive ? (
            <Button variant="outline" size="sm" className="gap-1" onClick={() => handlePublish(false)} disabled={visibilityMutation.isPending}>
              <EyeOff className="h-4 w-4" /> Unpublish
            </Button>
          ) : (
            <Button size="sm" className="gap-1" onClick={() => handlePublish(true)} disabled={visibilityMutation.isPending}>
              <Globe className="h-4 w-4" /> Publish
            </Button>
          )
        )}
        <Button variant="outline" size="sm" className="gap-1 text-destructive" onClick={() => setDeleteOpen(true)}>
          <Trash2 className="h-4 w-4" /> Delete
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-lg">Images</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {images.length ? images.map((img, i) => (
              <img
                key={i}
                src={resolveUploadUrl(img.imageUrl)}
                alt=""
                className="h-28 w-28 rounded-xl border object-cover"
              />
            )) : (
              <div className="flex h-28 w-28 items-center justify-center rounded-xl border bg-muted">
                <Package className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Details</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-muted-foreground">MRP – Max Retail Price</span><p className="font-medium">₹{product.mrp}</p></div>
              <div><span className="text-muted-foreground">DP – Distributor Price</span><p className="font-medium">₹{product.distributorPrice ?? 0}</p></div>
              <div><span className="text-muted-foreground">GST</span><p className="font-medium">{product.gstPercent ?? 0}%</p></div>
              <div><span className="text-muted-foreground">WP – Wholesale Price</span><p className="font-medium">₹{product.wholesalePrice ?? 0}</p></div>
              <div><span className="text-muted-foreground">RP – Retail Price</span><p className="font-medium">₹{product.retailPrice ?? 0}</p></div>
              <div><span className="text-muted-foreground">CP – Customer Price</span><p className="font-medium">₹{product.sellingPrice}</p></div>
              <div><span className="text-muted-foreground">DC – Delivery Charge</span><p className="font-medium">₹{product.deliveryCharge ?? 0}</p></div>
              <div><span className="text-muted-foreground">Distributor commission</span><p className="font-medium">{product.distributorCommissionPercent ?? 0}%</p></div>
              <div><span className="text-muted-foreground">Wholesale commission</span><p className="font-medium">{product.wholesaleCommissionPercent ?? 0}%</p></div>
              <div><span className="text-muted-foreground">Retail commission</span><p className="font-medium">{product.retailCommissionPercent ?? 0}%</p></div>
              <div><span className="text-muted-foreground">BV – Business Volume</span><p className="font-medium">{product.businessVolume}</p></div>
              <div><span className="text-muted-foreground">Store shelf stock</span><p className="font-medium">{product.stockQuantity}</p></div>
              <div><span className="text-muted-foreground">Unit</span><p className="font-medium">{product.unit}</p></div>
              <div><span className="text-muted-foreground">Visibility</span><p className="font-medium">{product.isActive ? "Live on storefront" : "Hidden"}</p></div>
              <div><span className="text-muted-foreground">Submitted</span><p className="font-medium">{new Date(product.createdAt).toLocaleDateString()}</p></div>
            </div>
            {product.description && (
              <div>
                <p className="text-muted-foreground mb-1">Description</p>
                <p>{product.description}</p>
              </div>
            )}
            {product.rejectionReason && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                <p className="text-xs font-medium text-destructive">Admin notes</p>
                <p className="mt-1">{product.rejectionReason}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <ChannelStockCard productId={productId} />
      </div>

      {canReview && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Review actions</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {mode && (
              <div className="space-y-2">
                <Label>{mode === "reject" ? "Rejection reason" : "Revision notes"}</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Explain what needs to change..."
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {!mode && (
                <>
                  <Button onClick={handleApprove} disabled={approveMutation.isPending}>
                    <Check className="mr-1 h-4 w-4" /> Approve & publish
                  </Button>
                  <Button variant="outline" onClick={() => setMode("revision")}>
                    <RotateCcw className="mr-1 h-4 w-4" /> Request revision
                  </Button>
                  <Button variant="destructive" onClick={() => setMode("reject")}>
                    <X className="mr-1 h-4 w-4" /> Reject
                  </Button>
                </>
              )}
              {mode === "revision" && (
                <>
                  <Button variant="outline" onClick={() => { setMode(null); setNotes(""); }}>Cancel</Button>
                  <Button onClick={() => handleAction("RequestRevision")} disabled={!notes.trim() || approveMutation.isPending}>
                    Send revision request
                  </Button>
                </>
              )}
              {mode === "reject" && (
                <>
                  <Button variant="outline" onClick={() => { setMode(null); setNotes(""); }}>Cancel</Button>
                  <Button variant="destructive" onClick={() => handleAction("Reject")} disabled={!notes.trim() || approveMutation.isPending}>
                    Confirm rejection
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {product.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the product from the platform catalog permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminProductDetail;
